import React, { useState } from 'react';

import Card from '../../components/Card';
import CreditCardInput from './inputs/CreditCardInput';
import ExpiryDateInput from './inputs/ExpiryDateInput';
import CVVInput from './inputs/CVVInput';
import TextInput from './inputs/TextInput';

import infoIcon from '../../assets/info.svg';
import closeIcon from '../../assets/close.svg';
import cvvInfoIcon from '../../assets/cvv-info.svg';
import PaymentFooter from './PaymentFooter';

const reg = new RegExp('^[0-9( |/)]+$');
const zipReg = new RegExp('^[a-z0-9(\\-)]{0,10}$');

export default function CreditCardForm({ onSubmit }) {
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expDate: '',
    cvv: '',
    firstName: '',
    lastName: '',
    address: '',
    zip: '',
  });
  const [cvvInfo, setCvvInfo] = useState(false);

  const [error, setError] = useState({
    number: null,
    expDate: null,
    cvv: null,
    firstName: null,
    lastName: null,
    address: null,
    zip: null,
  });

  const checkErrors = (cardDetails) => {
    const errors = [];
    const { number, expDate, cvv, firstName, lastName, address, zip } = cardDetails;

    if (number.length === 0 || number.length > 19) {
      errors.push('number');
    }

    if (expDate.length === 0 || expDate.length < 5) {
      errors.push('expDate');
    }

    if (cvv.length === 0 || cvv.length < 3) {
      errors.push('cvv');
    }

    if (firstName.length === 0) {
      errors.push('firstName');
    }

    if (lastName.length === 0) {
      errors.push('lastName');
    }

    if (address.length === 0) {
      errors.push('address');
    }

    if (zip.length === 0) {
      errors.push('zip');
    }

    return errors;
  };

  const btnDisabled = checkErrors(cardDetails).length !== 0;

  const handleNext = () => {
    const errors = checkErrors(cardDetails);
    const { number, expDate, cvv, firstName, lastName, address, zip } = cardDetails;

    setError(Object.fromEntries(errors.map((key) => [key, true])));

    if (!errors.length) {
      onSubmit({
        cardNumber: number.split(' ').join(''),
        expirationDate: expDate.split('/').join(''),
        cardCode: cvv,
        firstName: firstName,
        lastName: lastName,
        address: address,
        zip: zip,
      });
    }
  };

  const handleCardDetailsChange = ({ target }) => {
    if (
      !reg.test(target.value) &&
      target.value.length !== 0 &&
      target.name !== 'firstName' &&
      target.name !== 'lastName' &&
      target.name !== 'zip' &&
      target.name !== 'address'
    ) {
      return;
    }

    setError({ ...error, [target.name]: false });

    if (!zipReg.test(target.value) && target.name === 'zip') {
      return;
    }

    if (target.name === 'number') {
      const noSpaces = target.value.split(' ').join('');

      if (noSpaces.length >= 17) {
        return;
      }

      let newValue = '';

      for (let i = 0; i < noSpaces.length; i++) {
        newValue += noSpaces[i];
        if (i > 0 && (i + 1) % 4 === 0 && i !== noSpaces.length - 1) {
          newValue += ' ';
        }
      }

      target.value = newValue;
    }

    if (target.name === 'expDate') {
      const noSpaces = target.value.split('/').join('');
      if (noSpaces.length >= 5) {
        return;
      }

      if (
        noSpaces.length % 2 === 0 &&
        noSpaces.length < 4 &&
        target.value.length > cardDetails.expDate.length
      ) {
        target.value += '/';
      }
    }

    if (target.name === 'cvv' && target.value.length >= 5) {
      return;
    }

    setCardDetails({ ...cardDetails, [target.name]: target.value });
  };

  return (
    <>
      <Card step={4} showStep={false} title="Pay" CollapsedContent={CollapsedContent}>
        <div className="credit-card-details">
          <div className="card-details-wrapper">
            <div className="mb-12">
              <div className="label">Card number</div>
              <div className="number-input">
                <CreditCardInput
                  error={error.number}
                  value={cardDetails.number}
                  onChange={handleCardDetailsChange}
                />
              </div>
            </div>
            <div className="two-items mb-12">
              <div className="two-items-item">
                <div className="label">Expiry date</div>
                <div className="number-input">
                  <ExpiryDateInput
                    error={error.expDate}
                    value={cardDetails.expDate}
                    onChange={handleCardDetailsChange}
                  />
                </div>
              </div>
              <div className="two-items-item">
                <div className="label">
                  CVV{' '}
                  <img
                    src={cvvInfo ? closeIcon : infoIcon}
                    onClick={() => setCvvInfo(!cvvInfo)}
                    className="info-icon"
                    alt=""
                  />
                </div>
                <div className="number-input">
                  <CVVInput
                    error={error.cvv}
                    value={cardDetails.cvv}
                    onChange={handleCardDetailsChange}
                  />
                </div>
                {cvvInfo && (
                  <div className="cvv-info">
                    <div className="icon">
                      <img src={cvvInfoIcon} alt="info" />
                    </div>
                    <div className="text">
                      The CVV or CSC is the 3 or 4 digit number at the back of your card
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="two-items mb-12">
              <div className="two-items-item">
                <div className="label">First name</div>
                <div className="number-input">
                  <TextInput
                    name="firstName"
                    error={error.firstName}
                    value={cardDetails.firstName}
                    onChange={handleCardDetailsChange}
                    autoComplete="cc-given-name"
                  />
                </div>
              </div>
              <div className="two-items-item">
                <div className="label">Last name</div>
                <div className="number-input">
                  <TextInput
                    name="lastName"
                    error={error.lastName}
                    value={cardDetails.lastName}
                    onChange={handleCardDetailsChange}
                    autoComplete="cc-family-name"
                  />
                </div>
              </div>
            </div>

            <div className="mb-12">
              <div className="label">Address</div>
              <div className="number-input">
                <TextInput
                  name="address"
                  placeholder="Street and number"
                  error={error.address}
                  value={cardDetails.address}
                  onChange={handleCardDetailsChange}
                />
              </div>
            </div>

            <div className="two-items mb-12">
              <div className="two-items-item">
                <div className="label">Zip</div>
                <div className="number-input">
                  <TextInput
                    name="zip"
                    placeholder="Zip code"
                    error={error.zip}
                    value={cardDetails.zip}
                    onChange={handleCardDetailsChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <PaymentFooter text="Continue" onPay={handleNext} disabledStyle={btnDisabled} />
    </>
  );
}

function CollapsedContent() {
  return null;
}
